import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { AppUseSelector } from '../../redux/hooks'
import { updateAudioPronunciationSetting, AudioPronunciationSetting } from '../../redux/actions/storyAction'
import NotificationBox from '../../containers/form/NotificationBox'
import { SettingsOption, SettingsSection } from './SettingsSection'

// Labels and descriptions are translation keys.
const audioOptions: { label: string; value: AudioPronunciationSetting; description?: string }[] = [
  {
    label: 'settings:wordAudio.options.both.label',
    value: 'both',
    description: 'settings:wordAudio.options.both.description',
  },
  { label: 'settings:wordAudio.options.male.label', value: 'male_only' },
  { label: 'settings:wordAudio.options.female.label', value: 'female_only' },
  {
    label: 'settings:wordAudio.options.off.label',
    value: 'none',
    description: 'settings:wordAudio.options.off.description',
  },
]

const WordAudioSection = ({ id }: { id?: string }) => {
  const { t } = useTranslation('settings')
  const { audio_pronunciation_setting } = AppUseSelector(({ story }) => story)
  const [selectedSetting, setSelectedSetting] = useState<AudioPronunciationSetting>(audio_pronunciation_setting)
  const [isUpdating, setIsUpdating] = useState(false)
  const [notify, setNotify] = useState({ label: '', isError: false })

  useEffect(() => {
    setSelectedSetting(audio_pronunciation_setting)
  }, [audio_pronunciation_setting])

  const clearNotify = () => setNotify({ label: '', isError: false })

  const handleSettingChange = async (value: AudioPronunciationSetting) => {
    if (value === selectedSetting || isUpdating) return
    clearNotify()
    setIsUpdating(true)
    try {
      await updateAudioPronunciationSetting(value)
      setSelectedSetting(value)
      setNotify({ label: t('wordAudio.saved'), isError: false })
    } catch {
      setNotify({ label: t('wordAudio.saveError'), isError: true })
    } finally {
      setIsUpdating(false)
    }
  }

  return (
    <SettingsSection
      id={id}
      title={t('wordAudio.title')}
      note={t('wordAudio.note', { count: 12000 })}
    >
      <NotificationBox clearNotify={clearNotify} {...notify} />
      <div className='settings-options'>
        {audioOptions.map((option) => (
          <SettingsOption
            key={option.value}
            type='radio'
            name='audio-pronunciation'
            label={t(option.label)}
            description={option.description ? t(option.description) : undefined}
            checked={selectedSetting === option.value}
            disabled={isUpdating}
            onChange={() => handleSettingChange(option.value)}
          />
        ))}
      </div>
    </SettingsSection>
  )
}

export default WordAudioSection
